import { CompanyClientDataType } from '@upright/common/model/company-client-data/types';
import { AdditionalSearchType, HeaderSearchSelection } from '@upright/common/model/search/types';
import { type ChatProvider, DEFAULT_CHAT_PROVIDER, isChatProvider } from '@upright/common/chat/provider-capabilities';
import { Dictionary, get, isNil, pickBy } from 'lodash';
import { useState } from 'react';
import { ResponseLogin, User } from '../../../../common/iam/users/types';
import { ModelEntityReference } from '../../../../common/model/input/types';
import { isTeamUser } from '../../../../common/utils';
import {
  defaultNetImpactLensSelectionOptions,
  NetImpactLensSelectionOptions,
} from '../../upright/core/impact-lenses/net-impact/selection-state';
import { ComparisonRefsByUserEntityTypeAndId } from '../../upright/model/output/scores/comparison/types';
import { cleanViewSettingOverrides, ViewSettingOverrides } from '../view-settings/registry';
import { ToggleableComponent, ToggledUIComponents } from './types';

// PLT-4720: legacy localStorage keys for the access / refresh JWTs. The
// pen-test finding flagged storing JWTs in `localStorage` as exposed to XSS;
// auth now lives in the `up-access` / `up-refresh` HttpOnly cookies set by
// the backend on login / refresh / SSO redirect. These constants are
// retained for one release so `clearLegacyAuthTokensFromStorage` and
// `clearStorageExceptUiComponentStatuses` can purge any leftover keys when
// existing users load the new frontend, and so the migration helper can
// detect whether legacy tokens are present. The follow-up cleanup PR
// removes the constants and the helpers below.
const legacyAccessTokenKey = 'UPRIGHT/DEBUGGER/TOKEN';
const legacyRefreshTokenKey = 'UPRIGHT/DEBUGGER/REFRESH_TOKEN';
const userKey = 'UPRIGHT/DEBUGGER/USER';
const teamKey = 'UPRIGHT/DEBUGGER/TEAM';
const teamBrowserKey = 'UPRIGHT/DEBUGGER/TEAM_BROWSER';
const compareRefsKey = 'UPRIGHT/COMPARE';
const modelVersionKey = 'UPRIGHT/MODEL_VERSION';
const releaseKey = 'UPRIGHT/RELEASE';
const toggledComponentsKey = 'UPRIGHT/TOGGLED_COMPONENTS';
const searchFilterKey = 'UPRIGHT/SEARCH_FILTER';
// PLT-4824: replaced the legacy single `UPRIGHT/EXPERT_MODE` toggle with a
// per-feature "view settings" registry stored as a single JSON blob under
// `view.settings`. The blob holds only the keys whose value is `true` (so
// missing key === default OFF), tagged with a schema version. The legacy
// `UPRIGHT/EXPERT_MODE` key is intentionally NOT migrated — per the spec,
// existing expert-mode users go back to default OFF on cutover and re-enable
// the specific affordances they want from the new view-settings page.
const viewSettingsKey = 'view.settings';
const VIEW_SETTINGS_SCHEMA_VERSION = 1;
// Per-feature internal toggle for the chat. Predates the generic view-settings
// registry (PLT-4824); kept as its own key because the chat module has been
// migrating to lowercase namespaced storage keys and the panel surfaces a
// dedicated UI in the chat composer rather than a checkbox on the View
// settings page. See PLT-4475.
const chatInternalModeKey = 'UPRIGHT/CHAT_INTERNAL_MODE';
// Persisted collapsed/expanded state of the chat internal-mode debug panel.
// Saved separately from the toggle itself so the user's preferred panel state
// survives toggling internal mode off and back on.
const chatDebugPanelOpenKey = 'UPRIGHT/CHAT_DEBUG_PANEL_OPEN';
// Free-text "custom user instructions" blob set by team users from the chat
// debug panel and appended to the chat system prompt server-side (PLT-4492).
// Per-user, per-browser, not synced. The backend-friendly key
// (`chat.userInstructions`, lower-case-dotted) deliberately differs from the
// `UPRIGHT/...` ALL-CAPS scheme used by older entries: the chat module is
// gradually migrating to namespaced lowercase keys, and this is a greenfield
// addition so there's no compatibility cost. See PLT-4492 spec.
const chatUserInstructionsKey = 'chat.userInstructions';
/**
 * Hard limit on the user-instructions blob, mirrored on the backend's Joi
 * validator. Exposed for the textarea's `maxLength`/counter so the UI cannot
 * silently send a value the server would reject.
 */
export const MAX_CHAT_USER_INSTRUCTIONS_LENGTH = 4000;
// PLT-4507: persisted team-only chat model override (debug panel) and the
// matching reasoning toggle. Same lowercase-dotted naming convention as
// `chat.userInstructions` introduced in PLT-4492 — the chat module is
// gradually migrating away from the legacy `UPRIGHT/...` keys.
const chatDebugModelKey = 'chat.debugModel';
const chatDebugReasoningEnabledKey = 'chat.debugReasoningEnabled';
const chatDebugProviderKey = 'chat.debugProvider';
const sdgPureRevenueShareKey = 'UPRIGHT/SDG_PURE_REVENUE_SHARE_MODE';
const anonymousLikeKey = 'UPRIGHT/ANONYMOYS_LIKE';
const interestedInCompanyKey = 'UPRIGHT/INTERESTED_IN_COMPANY';
const netImpactLenseSelectionOptions = 'UPRIGHT/LENS_STATE/NET_IMPACT_LENS_SELECTION_OPTIONS';
const netImpactLensComparisonItems = 'UPRIGHT/LENS_STATE/NET_IMPACT_LENS_COMPARISON_ITEMS';
export const netImpactLensTipPopOverKey = 'UPRIGHT/LENS_STATE/NET_IMPACT_LENS_TIP_POPOVER_CHECKED';
export const comparisonModePopoverKey = 'UPRIGHT/LENS_STATE/NET_IMPACT_LENS_COMPARISON_MODE_POPOVER_CHECKED';
export const internalToolsBarKey = 'UPRIGHT/INTERNAL_TOOLS_BAR';

/**
 * PLT-4720: did this browser ever have legacy localStorage-stored auth
 * tokens? Used by `useLoadOwnInfo` to decide whether to call the
 * migration refresh (which re-issues the JWTs as cookies and then
 * clears the legacy keys).
 */
export function hasLegacyAuthTokensInStorage(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(legacyAccessTokenKey) !== null || localStorage.getItem(legacyRefreshTokenKey) !== null;
  } catch {
    return false;
  }
}

export function loadModelVersion() {
  return localStorage.getItem(modelVersionKey) ?? undefined;
}

export function saveModelVersion(version: string) {
  localStorage.setItem(modelVersionKey, version);
}

const nullReleaseValue = 'null';

export function loadRelease() {
  const value = localStorage.getItem(releaseKey);
  return value === nullReleaseValue ? null : value;
}

export function saveRelease(release: string | null) {
  localStorage.setItem(releaseKey, release ?? nullReleaseValue);
}

export type ViewSettingsBlob = ViewSettingOverrides;

interface PersistedViewSettings {
  schemaVersion: number;
  flags: ViewSettingsBlob;
}

/**
 * Load the per-feature view settings (PLT-4824). Returns an empty object when
 * the key is absent, malformed, or the persisted schema version doesn't match
 * the current one. We intentionally discard on schema-version mismatch rather
 * than attempting to migrate — the registry of keys is small and every key
 * back at its default is a safe, explainable failure mode.
 *
 * Note: this loader does NOT enforce the team-user gate. Apply the gate at
 * the hook layer (`useViewSetting`) so non-team users see all-off regardless
 * of the persisted blob (defence in depth).
 */
export function loadViewSettings(): ViewSettingsBlob {
  const raw = localStorage.getItem(viewSettingsKey);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedViewSettings>;
    if (parsed.schemaVersion !== VIEW_SETTINGS_SCHEMA_VERSION || !parsed.flags || typeof parsed.flags !== 'object') {
      return {};
    }
    return cleanViewSettingOverrides(parsed.flags);
  } catch {
    return {};
  }
}

/**
 * Persist the per-feature view settings (PLT-4824). Stores only the entries
 * that differ from their key's default (`true` for most keys, `false` for a
 * `defaultEnabled` one); a missing key is read back as its default. Saving an
 * empty blob clears the localStorage entry entirely so a reset state has no
 * leftover key.
 */
export function saveViewSettings(flags: ViewSettingsBlob) {
  const overrides = cleanViewSettingOverrides(flags);
  if (Object.keys(overrides).length === 0) {
    localStorage.removeItem(viewSettingsKey);
    return;
  }
  const blob: PersistedViewSettings = {
    schemaVersion: VIEW_SETTINGS_SCHEMA_VERSION,
    flags: overrides,
  };
  localStorage.setItem(viewSettingsKey, JSON.stringify(blob));
}

// TODO(plt-4475): when we migrate UI prefs to a server-side user-settings JSON
// blob, swap these four helpers to read/write that blob instead. They are
// deliberately shaped identically to load/saveExpertMode so the migration is a
// localised diff. Don't pre-build a generic registry for one flag — wait until
// the third per-feature toggle to extract the abstraction.
export function loadChatInternalMode() {
  return localStorage.getItem(chatInternalModeKey) === 'true';
}

export function saveChatInternalMode(enabled: boolean) {
  localStorage.setItem(chatInternalModeKey, enabled ? 'true' : 'false');
}

export function loadChatDebugPanelOpen() {
  return localStorage.getItem(chatDebugPanelOpenKey) === 'true';
}

export function saveChatDebugPanelOpen(open: boolean) {
  localStorage.setItem(chatDebugPanelOpenKey, open ? 'true' : 'false');
}

/**
 * Read the user-provided custom instructions blob (PLT-4492). Returns an
 * empty string when unset so callers can treat empty/unset uniformly — the
 * chat adapter only forwards the field to the server when non-empty after
 * trimming, so a stored empty string is a no-op on the wire.
 */
export function loadChatUserInstructions(): string {
  return localStorage.getItem(chatUserInstructionsKey) ?? '';
}

/**
 * Persist the user-provided custom instructions. Trimmed, capped at
 * `MAX_CHAT_USER_INSTRUCTIONS_LENGTH`, and a fully-empty value clears the key
 * (rather than storing an empty string) so the panel's "instructions active"
 * indicator turns off cleanly without an extra cleanup round-trip.
 */
export function saveChatUserInstructions(value: string) {
  const trimmed = value.slice(0, MAX_CHAT_USER_INSTRUCTIONS_LENGTH);
  if (trimmed.length === 0) {
    localStorage.removeItem(chatUserInstructionsKey);
    return;
  }
  localStorage.setItem(chatUserInstructionsKey, trimmed);
}

export function clearChatUserInstructions() {
  localStorage.removeItem(chatUserInstructionsKey);
}

/**
 * Stable wire keys for the chat model selector (PLT-4507). A SUBSET of the
 * backend's `ChatModelKey` allowlist in
 * `src/services/main/upserve/modules/chat/model-options.ts`, not a mirror of
 * it: entries here must exist there (the backend Joi validator rejects unknown
 * values, so an extra key surfaces as a 400), but the reverse does not hold.
 * Backend rows flagged `workbenchOnly` — currently Fable 5.1, PLT-6636 — are
 * deliberately excluded, because they are added for AI-test-workbench
 * benchmarking and chat should not offer a model whose cost and latency on real
 * chat traffic nobody has measured. Do not "restore lockstep" by adding one;
 * check the flag first.
 */
export type ChatDebugModelKey = 'opus' | 'sonnet' | 'haiku';

/** Default model the chat uses when the user hasn't picked one. */
export const DEFAULT_CHAT_DEBUG_MODEL: ChatDebugModelKey = 'opus';

const ALL_CHAT_DEBUG_MODELS: readonly ChatDebugModelKey[] = ['opus', 'sonnet', 'haiku'];

function isChatDebugModelKey(value: string | null): value is ChatDebugModelKey {
  return value !== null && (ALL_CHAT_DEBUG_MODELS as readonly string[]).includes(value);
}

/**
 * Read the persisted chat model override (PLT-4507). Returns
 * `DEFAULT_CHAT_DEBUG_MODEL` when unset OR when the stored value is not a
 * known key — the latter shields against stale localStorage entries
 * lingering after the allowlist shrinks (e.g. a future Sonnet→Sonnet 5
 * rename) without surfacing a confusing UI state.
 */
export function loadChatDebugModel(): ChatDebugModelKey {
  const raw = localStorage.getItem(chatDebugModelKey);
  return isChatDebugModelKey(raw) ? raw : DEFAULT_CHAT_DEBUG_MODEL;
}

/** Persist the chat model override. Storing the default clears the key
 *  rather than writing it back, so a clean state has no leftover entry. */
export function saveChatDebugModel(value: ChatDebugModelKey) {
  if (value === DEFAULT_CHAT_DEBUG_MODEL) {
    localStorage.removeItem(chatDebugModelKey);
    return;
  }
  localStorage.setItem(chatDebugModelKey, value);
}

/**
 * Read the persisted reasoning toggle (PLT-4507). Defaults to `true` —
 * the chat historically ran with `thinking: { type: 'adaptive' }` always
 * on, and a missing key represents that legacy behaviour. Only an
 * explicit `'false'` flips it off.
 */
export function loadChatDebugReasoningEnabled(): boolean {
  const raw = localStorage.getItem(chatDebugReasoningEnabledKey);
  // Missing key → default ON. We only treat the literal string 'false'
  // as off; anything else (including legacy 'true' values, malformed
  // data, etc.) keeps the legacy ON behaviour.
  return raw !== 'false';
}

/** Persist the reasoning toggle. The default (`true`) is stored as a
 *  removed key — `loadChatDebugReasoningEnabled` already returns true
 *  when the key is missing. */
export function saveChatDebugReasoningEnabled(enabled: boolean) {
  if (enabled) {
    localStorage.removeItem(chatDebugReasoningEnabledKey);
    return;
  }
  localStorage.setItem(chatDebugReasoningEnabledKey, 'false');
}

/**
 * Chat LLM provider override (PLT-5035). The provider union and the
 * default both come from `@upright/common/chat/provider-capabilities`, the
 * single source shared with the backend — so the persisted value can never
 * drift from what the backend Joi validator accepts. `ChatDebugProviderKey`
 * is kept as a local alias so existing call sites (debug panel, adapter)
 * read unchanged. `'anthropic'` is the default US Anthropic API; `'bedrock-eu'`
 * is the EU data-residency path (Claude on Bedrock, eu-west-1). This is the
 * staff EU testing switch: honoured server-side for team users only. A
 * non-team user's provider follows their `USE_AI_CHAT_EU_RESIDENCY` grant
 * instead (PLT-7018).
 */
export type ChatDebugProviderKey = ChatProvider;

/** Default value of the staff EU testing switch. */
export const DEFAULT_CHAT_DEBUG_PROVIDER: ChatDebugProviderKey = DEFAULT_CHAT_PROVIDER;

/**
 * Read the persisted chat provider override (PLT-5035). Returns
 * `DEFAULT_CHAT_DEBUG_PROVIDER` when unset OR when the stored value is not a
 * known key — the latter shields against stale localStorage entries lingering
 * after the allowlist changes, mirroring `loadChatDebugModel`.
 */
export function loadChatDebugProvider(): ChatDebugProviderKey {
  // Coerce the `null` "key absent" sentinel to `undefined` so the
  // `isChatProvider` type guard narrows `raw` directly (the guard accepts
  // `string | undefined`).
  const raw = localStorage.getItem(chatDebugProviderKey) ?? undefined;
  return isChatProvider(raw) ? raw : DEFAULT_CHAT_DEBUG_PROVIDER;
}

/** Persist the chat provider override. Storing the default clears the key
 *  rather than writing it back, so a clean state has no leftover entry. */
export function saveChatDebugProvider(value: ChatDebugProviderKey) {
  if (value === DEFAULT_CHAT_DEBUG_PROVIDER) {
    localStorage.removeItem(chatDebugProviderKey);
    return;
  }
  localStorage.setItem(chatDebugProviderKey, value);
}

export function loadSdgPureRevenueShareMode() {
  return localStorage.getItem(sdgPureRevenueShareKey) === 'true';
}

export function saveSdgPureRevenueShareMode(enabled: boolean) {
  localStorage.setItem(sdgPureRevenueShareKey, enabled ? 'true' : 'false');
}

export function getUsername() {
  return localStorage.getItem(userKey) || '';
}

function saveUsername(username: string) {
  localStorage.setItem(userKey, username);
}

export function getNetImpactLensSelectionOptions() {
  const itemString = localStorage.getItem(netImpactLenseSelectionOptions);
  return itemString ? (JSON.parse(itemString) as NetImpactLensSelectionOptions) : defaultNetImpactLensSelectionOptions;
}

export function saveNetImpactLenseSelectionOptions(options: NetImpactLensSelectionOptions) {
  const cleaned = pickBy(options, v => !isNil(v));
  localStorage.setItem(netImpactLenseSelectionOptions, JSON.stringify(cleaned));
}

export function getTeamSession() {
  return localStorage.getItem(teamKey) === 'true';
}

function saveTeamSession(team: boolean) {
  localStorage.setItem(teamKey, team ? 'true' : 'false');
}

function saveTeamBrowser(team: boolean) {
  // Remember forever if browser has ever logged in as team (to bypass user tracking)
  if (team) {
    localStorage.setItem(teamBrowserKey, 'true');
  }
}

export function getTeamBrowser() {
  return localStorage.getItem(teamBrowserKey) === 'true';
}

export function saveUser(user: ResponseLogin | User) {
  saveUsername(user.username);
  const teamSession = isTeamUser(user);
  saveTeamSession(teamSession);
  saveTeamBrowser(teamSession);
  // PLT-4720: deliberately no longer write `accessToken` / `refreshToken`
  // to localStorage even when the login response body still contains them.
  // The backend now sets the JWTs as `up-access` / `up-refresh` HttpOnly
  // cookies; the body fields are kept for one release of legacy clients
  // and are ignored here.
}

/**
 * PLT-4720: clear the legacy localStorage-stored JWTs left over from
 * before the cookie migration. The cookie itself is cleared server-side
 * by `POST /api/logout`, which the caller (`removeTokenFromStorageAndForceNewLogin`)
 * also triggers. Kept for one release as a defensive cleanup; the
 * follow-up PR removes the function and the two `removeItem` calls.
 */
export function clearLegacyAuthTokensFromStorage() {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(legacyAccessTokenKey);
    localStorage.removeItem(legacyRefreshTokenKey);
  } catch {
    // Ignore (sandboxed iframe / disabled storage).
  }
}

export function clearStorageExceptUiComponentStatuses() {
  // Includes the legacy JWT keys (PLT-4720) — defensive cleanup for one
  // release. The current source of truth is the HttpOnly cookies.
  localStorage.removeItem(legacyAccessTokenKey);
  localStorage.removeItem(legacyRefreshTokenKey);
  localStorage.removeItem(userKey);
  localStorage.removeItem(teamKey);
  localStorage.removeItem(compareRefsKey);
  localStorage.removeItem(modelVersionKey);
  removeAnonymousLikesAndInterests();
  // localStorage.removeItem(toggledComponentsKey);
}

export function saveCompareRefs(refs: ModelEntityReference[]) {
  localStorage.setItem(compareRefsKey, JSON.stringify(refs));
}

export function loadCompareRefs(): ModelEntityReference[] {
  const value = localStorage.getItem(compareRefsKey);
  if (!value) {
    return [];
  }
  return JSON.parse(value) as ModelEntityReference[];
}

export function saveToggledComponentStatus(component: ToggleableComponent, toggled: boolean) {
  const emptyComponents: Partial<ToggledUIComponents> = {};
  const toggledComponents = loadToggledComponents() || emptyComponents;
  toggledComponents[component] = toggled;
  localStorage.setItem(toggledComponentsKey, JSON.stringify(toggledComponents));
}

export function loadToggledComponents(): ToggledUIComponents | undefined {
  const value = localStorage.getItem(toggledComponentsKey);
  if (!value) {
    return;
  }
  return JSON.parse(value) as ToggledUIComponents;
}

export function loadSearchFilter(): HeaderSearchSelection | undefined {
  const value = localStorage.getItem(searchFilterKey);
  if (!value) {
    return AdditionalSearchType.ALL;
  }
  return value as HeaderSearchSelection;
}

export function saveSearchFilter(filter: HeaderSearchSelection | undefined) {
  if (filter) {
    localStorage.setItem(searchFilterKey, filter);
  } else {
    localStorage.removeItem(searchFilterKey);
  }
}

type LikedByKey = Dictionary<boolean | undefined>;

export function saveAnonymousLike(liked: boolean, companyId: string, dataType: CompanyClientDataType, id: number) {
  const unparsed = localStorage.getItem(anonymousLikeKey) || '{}';
  const likeData = JSON.parse(unparsed) as LikedByKey;
  likeData[getAnonymousLikeKey(companyId, dataType, id)] = liked;
  localStorage.setItem(anonymousLikeKey, JSON.stringify(likeData));
}

export function isLikedAnonymously(companyId: string, dataType: CompanyClientDataType, id: number) {
  const unparsed = localStorage.getItem(anonymousLikeKey) || '{}';
  const likeData = JSON.parse(unparsed) as LikedByKey;
  return get(likeData, getAnonymousLikeKey(companyId, dataType, id), false);
}

function getAnonymousLikeKey(companyId: string, dataType: CompanyClientDataType, id: number) {
  return `${companyId}--${dataType}--${id}`;
}

export function removeAnonymousLikesAndInterests() {
  localStorage.removeItem(anonymousLikeKey);
  localStorage.removeItem(interestedInCompanyKey);
}

export function saveInterestedAnonymouslyInCompany(interested: boolean, companyId: string) {
  const unparsed = localStorage.getItem(interestedInCompanyKey) || '{}';
  const likeData = JSON.parse(unparsed) as LikedByKey;
  likeData[companyId] = interested;
  localStorage.setItem(interestedInCompanyKey, JSON.stringify(likeData));
}

export function isAnonymouslyInterestedInCompany(companyId: string) {
  const unparsed = localStorage.getItem(interestedInCompanyKey) || '{}';
  const likeData = JSON.parse(unparsed) as LikedByKey;
  return get(likeData, companyId, false);
}

export function useRememberBoolean(storageKey: string, defaultState?: boolean) {
  const [state, setState] = useState(defaultState ?? localStorage.getItem(storageKey) === 'true');

  function dispatch(value: boolean) {
    setState(value);
    localStorage.setItem(storageKey, `${value}`);
  }
  return [state, dispatch] as const;
}

export function getComparisonItems(): ComparisonRefsByUserEntityTypeAndId {
  const unparsed = localStorage.getItem(netImpactLensComparisonItems) || '{}';
  return JSON.parse(unparsed) as ComparisonRefsByUserEntityTypeAndId;
}

export function saveComparisonItems(items: ComparisonRefsByUserEntityTypeAndId) {
  localStorage.setItem(netImpactLensComparisonItems, JSON.stringify(items));
}

function getBannerDismissalKey(bannerId: string) {
  return `UPRIGHT/BANNER_DISMISSED/${bannerId}`;
}

export function saveBannerDismissal(bannerId: string) {
  localStorage.setItem(getBannerDismissalKey(bannerId), 'dismissed');
}

export function clearBannerDismissal(bannerId: string) {
  localStorage.removeItem(getBannerDismissalKey(bannerId));
}

export function isBannerDismissed(bannerId: string) {
  return localStorage.getItem(getBannerDismissalKey(bannerId)) !== null;
}
